import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal } from "./Reveal";

const PHOTOS = [
  {
    src: "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcG91cmluZyUyMHNhdWNlJTIwcGxhdGluZyUyMGZpbmUlMjBkaW5pbmd8ZW58MHx8fHwxNzkyNzk3NTU5fDA&ixlib=rb-4.1.0&q=85",
    alt: "Chef pouring sauce over a plated dish",
    caption: "The final pour, tableside",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    src: "https://images.pexels.com/photos/26586541/pexels-photo-26586541.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Caviar plating detail on a fine dining dish",
    caption: "Caviar, placed by hand",
    span: "",
  },
  {
    src: "https://images.unsplash.com/photo-1773005695300-14b62bc85ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Nzd8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMHRhYmxlc2NhcGUlMjBjYW5kbGVzJTIwZmxvcmFsfGVufDB8fHx8MTc5Mjc5NzU1OXww&ixlib=rb-4.1.0&q=85",
    alt: "Elegant wedding tablescape with candles and florals",
    caption: "An estate table, dressed",
    span: "md:row-span-2",
  },
  {
    src: "https://images.pexels.com/photos/16823275/pexels-photo-16823275.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Candlelit banquet table setting",
    caption: "Candlelight service",
    span: "",
  },
  {
    src: "https://images.unsplash.com/photo-1790029410326-8776ebfbb434?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6NjZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYW5hcGVzJTIwc3Bvb25zJTIwaG9ycyUyMGQlMjBvZXV2cmVzfGVufDB8fHx8MTc5Mjc5NzU1OXww&ixlib=rb-4.1.0&q=85",
    alt: "Luxury canapés served on spoons",
    caption: "Canapés, first circuit",
    span: "",
  },
  {
    src: "https://images.pexels.com/photos/32774004/pexels-photo-32774004.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Catering appetizers arranged for service",
    caption: "Passed and plated",
    span: "md:col-span-2",
  },
  {
    src: "https://images.pexels.com/photos/5039342/pexels-photo-5039342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Salmon skewers prepared for an event",
    caption: "From the raw bar",
    span: "",
  },
  {
    src: "https://images.pexels.com/photos/1114425/pexels-photo-1114425.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Candlelit glassware at a dinner reception",
    caption: "Glassware at dusk",
    span: "md:col-span-2",
  },
];

export const Gallery = () => {
  const [selected, setSelected] = useState(null);

  const step = (dir) =>
    setSelected((i) => (i + dir + PHOTOS.length) % PHOTOS.length);

  return (
    <section id="gallery" data-testid="photo-gallery-section" className="bg-linen py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-16 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ink-mute">
              The Gallery
            </p>
            <h2 className="max-w-xl font-serif text-3xl leading-tight tracking-tight text-noir sm:text-4xl lg:text-5xl">
              Evenings, <span className="italic text-truffle">remembered</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-ink-mute">
              Fragments from recent tables — weddings, estates and late-night
              tastings. Tap any frame to view it full-screen.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:auto-rows-[210px]">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={(i % 4) * 0.08} className={p.span}>
              <button
                data-testid={`gallery-image-${i + 1}`}
                onClick={() => setSelected(i)}
                className="group relative block h-48 w-full overflow-hidden rounded-2xl border border-noir/10 transition-[border-color,box-shadow] duration-500 hover:border-gold/60 hover:shadow-[0_25px_50px_-20px_rgba(92,64,51,0.35)] md:h-full"
                aria-label={`View photo: ${p.caption}`}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                />
                <span className="absolute inset-x-0 bottom-0 translate-y-3 bg-noir/55 px-4 py-2.5 text-left font-serif text-sm italic text-bone opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {p.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-noir/92 p-5 backdrop-blur-md sm:p-12"
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
          >
            <button
              data-testid="lightbox-close-button"
              onClick={() => setSelected(null)}
              aria-label="Close photo viewer"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-bone/25 text-bone transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <X size={18} />
            </button>
            <button
              data-testid="lightbox-prev-button"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone/25 text-bone transition-colors duration-300 hover:border-gold hover:text-gold sm:left-8"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="lightbox-next-button"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone/25 text-bone transition-colors duration-300 hover:border-gold hover:text-gold sm:right-8"
            >
              <ChevronRight size={18} />
            </button>
            <motion.figure
              key={selected}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full"
            >
              <img
                src={PHOTOS[selected].src}
                alt={PHOTOS[selected].alt}
                data-testid="lightbox-image"
                className="max-h-[76vh] w-auto max-w-full rounded-2xl border border-bone/15 object-contain"
              />
              <figcaption className="mt-4 text-center font-serif text-lg italic text-bone/80">
                {PHOTOS[selected].caption}
                <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.25em] text-bone/40">
                  {selected + 1} / {PHOTOS.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
